/**
 * Fechas con zona horaria sobre Intl, sin librería de fechas.
 *
 * Cada evento guarda la zona IANA del dispositivo que lo registró, así que una
 * cena apuntada a las 22:00 en Canarias sigue siendo del mismo día cuando se
 * lee desde la península.
 */

export type TimezoneMode = 'device' | 'fixed'

export interface TimezoneSetting {
  mode: TimezoneMode
  /** Zona del hogar: se usa con mode 'fixed' y como respaldo. */
  fixed: string
}

export const DEFAULT_TIMEZONE = 'Europe/Madrid'

/** 'YYYY-MM-DD' en alguna zona. Nuestra unidad de "un día" en todas partes. */
export type DayKey = string

/** 'YYYY-MM-DD' del lunes de esa semana. La unidad de planificación. */
export type WeekKey = string

export interface ZonedParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  dayKey: DayKey
  /** 1 = lunes … 7 = domingo. */
  weekday: number
}

const formatterCache = new Map<string, Intl.DateTimeFormat>()

function partsFormatter(tz: string): Intl.DateTimeFormat {
  let fmt = formatterCache.get(tz)
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      weekday: 'short',
    })
    formatterCache.set(tz, fmt)
  }
  return fmt
}

const WEEKDAY_INDEX: Record<string, number> = {
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
  Sun: 7,
}

export const WEEKDAY_LABELS = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'] as const

export function deviceTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE
  } catch {
    return DEFAULT_TIMEZONE
  }
}

export function isValidTimezone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: tz })
    return true
  } catch {
    return false
  }
}

/** La zona en la que hay que leer un evento, respetando el ajuste del hogar. */
export function zoneFor(eventTz: string | undefined, setting: TimezoneSetting): string {
  if (setting.mode === 'fixed') return setting.fixed
  return eventTz && isValidTimezone(eventTz) ? eventTz : setting.fixed
}

export function toInstant(value: Date | string | number): number {
  if (value instanceof Date) return value.getTime()
  if (typeof value === 'number') return value
  return Date.parse(value)
}

export function zonedParts(value: Date | string | number, tz: string): ZonedParts {
  const parts = partsFormatter(tz).formatToParts(new Date(toInstant(value)))
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '0'

  const year = Number(get('year'))
  const month = Number(get('month'))
  const day = Number(get('day'))

  return {
    year,
    month,
    day,
    hour: Number(get('hour')) % 24,
    minute: Number(get('minute')),
    dayKey: `${pad(year, 4)}-${pad(month)}-${pad(day)}`,
    weekday: WEEKDAY_INDEX[get('weekday')] ?? 1,
  }
}

export function dayKeyOf(value: Date | string | number, tz: string): DayKey {
  return zonedParts(value, tz).dayKey
}

function pad(n: number, width = 2): string {
  return String(Math.abs(n)).padStart(width, '0')
}

/** Desfase de `tz` en un instante dado, en milisegundos. */
function offsetAt(ts: number, tz: string): number {
  const p = zonedParts(ts, tz)
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute)
  return asUtc - Math.floor(ts / 60_000) * 60_000
}

/** Instante UTC de una hora de reloj local. Contempla los cambios de hora. */
export function zonedTimeToInstant(
  tz: string,
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
): number {
  const guess = Date.UTC(year, month - 1, day, hour, minute)
  const firstOffset = offsetAt(guess, tz)
  let ts = guess - firstOffset
  const secondOffset = offsetAt(ts, tz)
  if (secondOffset !== firstOffset) ts = guess - secondOffset
  return ts
}

export function parseDayKey(dayKey: DayKey): { year: number; month: number; day: number } {
  const [y, m, d] = dayKey.split('-').map(Number)
  return { year: y ?? 1970, month: m ?? 1, day: d ?? 1 }
}

export function startOfDay(dayKey: DayKey, tz: string): number {
  const { year, month, day } = parseDayKey(dayKey)
  return zonedTimeToInstant(tz, year, month, day, 0, 0)
}

export function endOfDay(dayKey: DayKey, tz: string): number {
  return startOfDay(addDays(dayKey, 1), tz)
}

/**
 * Lo que dura ese día de verdad, en minutos.
 *
 * No son 1440 siempre: los días en que cambia la hora duran 23 o 25 horas, y
 * cualquier cuenta que asuma un día fijo se desvía una hora entera esos días.
 */
export function dayLengthMinutes(dayKey: DayKey, tz: string): number {
  return (endOfDay(dayKey, tz) - startOfDay(dayKey, tz)) / 60_000
}

export function addDays(dayKey: DayKey, days: number): DayKey {
  const { year, month, day } = parseDayKey(dayKey)
  const d = new Date(Date.UTC(year, month - 1, day))
  d.setUTCDate(d.getUTCDate() + days)
  return `${pad(d.getUTCFullYear(), 4)}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
}

export function diffDays(from: DayKey, to: DayKey): number {
  const a = parseDayKey(from)
  const b = parseDayKey(to)
  const ms = Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)
  return Math.round(ms / 86_400_000)
}

/** Las semanas van de lunes a domingo. */
export function startOfWeek(dayKey: DayKey): WeekKey {
  const { year, month, day } = parseDayKey(dayKey)
  const d = new Date(Date.UTC(year, month - 1, day))
  const weekday = d.getUTCDay() === 0 ? 7 : d.getUTCDay()
  return addDays(dayKey, 1 - weekday)
}

export function weekDays(weekKey: WeekKey): DayKey[] {
  return Array.from({ length: 7 }, (_, i) => addDays(weekKey, i))
}

export function dayKeysBetween(from: DayKey, to: DayKey): DayKey[] {
  const keys: DayKey[] = []
  for (let key = from; diffDays(key, to) >= 0; key = addDays(key, 1)) keys.push(key)
  return keys
}

export function weekdayOf(dayKey: DayKey): number {
  const { year, month, day } = parseDayKey(dayKey)
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay()
  return weekday === 0 ? 7 : weekday
}

export function weekdayLabel(dayKey: DayKey): string {
  return WEEKDAY_LABELS[weekdayOf(dayKey) - 1] ?? ''
}
